import React, { useState, useEffect } from 'react';
import { useSignUp, useSignIn } from '@clerk/clerk-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, AlertCircle, CheckCircle, Mail, Building } from 'lucide-react';
import { Button } from '@/components/common/ui/Button';
import { Input } from '@/components/common/ui/Input';

const AcceptInvitation: React.FC = () => {
    const { signUp, setActive: setActiveSignUp, isLoaded: signUpLoaded } = useSignUp();
    const { signIn, setActive: setActiveSignIn, isLoaded: signInLoaded } = useSignIn();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [invitationEmail, setInvitationEmail] = useState('');
    const [organizationName, setOrganizationName] = useState('');

    // Get both the invitation ticket and status from URL parameters
    const ticket = searchParams.get('__clerk_ticket');
    const accountStatus = searchParams.get('__clerk_status');

    const isLoaded = signUpLoaded && signInLoaded;

    useEffect(() => {
        if (!ticket) {
            setError('Invalid or missing invitation link');
            return;
        }

        if (!accountStatus) {
            setError('Invalid invitation status');
            return;
        }

        // Handle automatic sign-in if status is 'complete'
        if (accountStatus === 'complete') {
            setSuccess(true);
            setTimeout(() => {
                navigate('/app');
            }, 1500);
            return;
        }

        // Handle existing user sign-in flow
        if (accountStatus === 'sign_in' && isLoaded && signIn) {
            handleExistingUserSignIn();
            return;
        }

        // Try to decode the JWT ticket to extract email and organization info
        try {
            const parts = ticket.split('.');
            if (parts.length === 3) {
                const payload = JSON.parse(atob(parts[1]));
                console.log('Invitation payload:', payload);

                if (payload.email) {
                    setInvitationEmail(payload.email);
                }

                if (payload.organization_name || payload.org_name) {
                    setOrganizationName(payload.organization_name || payload.org_name);
                }
            }
        } catch (decodeError) {
            console.log('Could not decode invitation details:', decodeError);
        }
    }, [ticket, accountStatus, isLoaded, signIn, navigate]);

    const handleExistingUserSignIn = async () => {
        if (!signIn || !ticket) return;

        try {
            setLoading(true);
            const signInAttempt = await signIn.create({
                strategy: 'ticket',
                ticket: ticket,
            });

            if (signInAttempt.status === 'complete') {
                await setActiveSignIn({ session: signInAttempt.createdSessionId });
                setSuccess(true);
                setTimeout(() => {
                    navigate('/app');
                }, 1500);
            } else {
                console.error('Sign-in requires additional steps:', signInAttempt);
                setError('Sign-in requires additional verification. Please check your email.');
            }
        } catch (err: any) {
            console.error('Sign-in error:', err);
            setError(err.errors?.[0]?.message || 'Failed to sign in with invitation.');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (error) {
            setError('');
        }
    };

    const validateForm = () => {
        if (!formData.password.trim()) {
            return 'Password is required';
        }

        if (formData.password.length < 8) {
            return 'Password must be at least 8 characters long';
        }

        if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
            return 'Password must contain uppercase, lowercase, and number';
        }

        if (formData.password !== formData.confirmPassword) {
            return 'Passwords do not match';
        }

        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Only validate form for sign-up flow
        if (accountStatus === 'sign_up') {
            const validationError = validateForm();
            if (validationError) {
                setError(validationError);
                return;
            }
        }

        if (!isLoaded) {
            setError('Authentication service is loading. Please wait.');
            return;
        }

        if (!signUp) {
            setError('Sign up service is not available. Please refresh the page.');
            return;
        }

        if (!ticket) {
            setError('Invalid invitation ticket.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Create the user account using the invitation ticket
            // Note: For ticket strategy, only pass ticket and password
            // The email and name are automatically extracted from the ticket
            const signUpAttempt = await signUp.create({
                strategy: 'ticket',
                ticket: ticket,
                password: formData.password,
            });

            if (signUpAttempt.status === 'complete') {
                if (signUpAttempt.emailAddress && !invitationEmail) {
                    setInvitationEmail(signUpAttempt.emailAddress);
                }

                if (setActiveSignUp && signUpAttempt.createdSessionId) {
                    await setActiveSignUp({ session: signUpAttempt.createdSessionId });
                }

                setSuccess(true);
                setTimeout(() => {
                    navigate('/app');
                }, 2000);
            } else {
                console.log('Sign-up requires additional steps:', signUpAttempt);
                setError('Additional verification required. Please check your email.');
            }
        } catch (err: any) {
            console.error('Sign-up error:', err);
            setError(err.errors?.[0]?.message || 'Failed to create account. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Show loading state while Clerk initializes
    if (!isLoaded) {
        return (
            <div className="w-full text-center space-y-6">
                <div className="w-16 h-16 bg-primary-500/10 rounded-full flex items-center justify-center mx-auto">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                </div>
                <div className="space-y-2">
                    <h1 className="text-2xl font-grotesk font-bold text-text-primary-light dark:text-text-primary-dark">
                        Loading...
                    </h1>
                    <p className="text-text-secondary-light dark:text-text-secondary-dark">
                        Initializing authentication service.
                    </p>
                </div>
            </div>
        );
    }

    // Show invalid invitation message
    if (!ticket || !accountStatus) {
        return (
            <div className="w-full text-center space-y-6">
                <div className="w-16 h-16 bg-error-50 dark:bg-error-950 rounded-full flex items-center justify-center mx-auto">
                    <AlertCircle className="h-8 w-8 text-error-500" />
                </div>
                <div className="space-y-2">
                    <h1 className="text-2xl font-grotesk font-bold text-text-primary-light dark:text-text-primary-dark">
                        Invalid Invitation
                    </h1>
                    <p className="text-text-secondary-light dark:text-text-secondary-dark">
                        This invitation link is invalid or has expired. Please contact your administrator for a new invitation.
                    </p>
                </div>
                <Button
                    variant="link"
                    onClick={() => navigate('/auth/login')}
                >
                    Back to Login
                </Button>
            </div>
        );
    }

    // Show processing state for existing user sign-in
    if (accountStatus === 'sign_in') {
        return (
            <div className="w-full text-center space-y-6">
                <div className="w-16 h-16 bg-primary-500/10 rounded-full flex items-center justify-center mx-auto">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                </div>
                <div className="space-y-2">
                    <h1 className="text-2xl font-grotesk font-bold text-text-primary-light dark:text-text-primary-dark">
                        Signing you in...
                    </h1>
                    <p className="text-text-secondary-light dark:text-text-secondary-dark">
                        You already have an account. Signing you in to the organization.
                    </p>
                    {error && (
                        <div className="flex items-center justify-center space-x-2 p-3 bg-error-50 dark:bg-error-950 border border-error-200 dark:border-error-800 rounded-lg">
                            <AlertCircle className="h-4 w-4 text-error-600 dark:text-error-400" />
                            <span className="text-sm text-error-600 dark:text-error-400">
                                {error}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Show success message
    if (success || accountStatus === 'complete') {
        return (
            <div className="w-full text-center space-y-6">
                <div className="w-16 h-16 bg-success-500/10 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle className="h-8 w-8 text-success-500" />
                </div>
                <div className="space-y-2">
                    <h1 className="text-2xl font-grotesk font-bold text-text-primary-light dark:text-text-primary-dark">
                        Welcome to ThreatScope!
                    </h1>
                    <p className="text-text-secondary-light dark:text-text-secondary-dark">
                        {accountStatus === 'complete'
                            ? 'You have successfully joined the organization.'
                            : 'Your account has been created successfully.'
                        }
                    </p>
                    {invitationEmail && (
                        <div className="flex items-center justify-center space-x-2 text-sm text-text-muted-light dark:text-text-muted-dark">
                            <Mail className="h-4 w-4" />
                            <span>{invitationEmail}</span>
                        </div>
                    )}
                    {organizationName && (
                        <div className="flex items-center justify-center space-x-2 text-sm text-text-muted-light dark:text-text-muted-dark">
                            <Building className="h-4 w-4" />
                            <span>{organizationName}</span>
                        </div>
                    )}
                    <p className="text-sm text-text-muted-light dark:text-text-muted-dark">
                        Redirecting to your dashboard...
                    </p>
                </div>
            </div>
        );
    }

    // Show sign-up form only for new users (accountStatus === 'sign_up')
    if (accountStatus !== 'sign_up') {
        return (
            <div className="w-full text-center space-y-6">
                <div className="w-16 h-16 bg-warning-500/10 rounded-full flex items-center justify-center mx-auto">
                    <AlertCircle className="h-8 w-8 text-warning-500" />
                </div>
                <div className="space-y-2">
                    <h1 className="text-2xl font-grotesk font-bold text-text-primary-light dark:text-text-primary-dark">
                        Unexpected Status
                    </h1>
                    <p className="text-text-secondary-light dark:text-text-secondary-dark">
                        The invitation status "{accountStatus}" is not recognized. Please contact support.
                    </p>
                </div>
                <Button
                    variant="link"
                    onClick={() => navigate('/auth/login')}
                >
                    Back to Login
                </Button>
            </div>
        );
    }

    return (
        <div className="w-full space-y-8">
            {/* Header */}
            <div className="text-center space-y-3">
                <h1 className="text-3xl font-grotesk font-bold text-text-primary-light dark:text-text-primary-dark">
                    Complete Your Account
                </h1>
                <div className="space-y-2">
                    <p className="text-text-secondary-light dark:text-text-secondary-dark">
                        You've been invited to join {organizationName || 'the organization'}.
                        Please set up your password to complete your account.
                    </p>
                    {invitationEmail && (
                        <div className="flex items-center justify-center space-x-2 text-sm text-primary-600 dark:text-primary-400">
                            <Mail className="h-4 w-4" />
                            <span>{invitationEmail}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Registration Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* General Error */}
                {error && (
                    <div className="flex items-center space-x-2 p-3 bg-error-50 dark:bg-error-950 border border-error-200 dark:border-error-800 rounded-lg">
                        <AlertCircle className="h-4 w-4 text-error-600 dark:text-error-400" />
                        <span className="text-sm text-error-600 dark:text-error-400">
                            {error}
                        </span>
                    </div>
                )}

                {/* Email Field (Read-only if we have it from invitation) */}
                {invitationEmail && (
                    <Input
                        label="Email Address"
                        type="email"
                        value={invitationEmail}
                        leftIcon={<Mail className="h-4 w-4" />}
                        disabled
                        readOnly
                        helperText="This email address is from your invitation"
                        variant="filled"
                    />
                )}

                {/* Organization Information */}
                {organizationName && (
                    <div className="bg-background-light-elevated dark:bg-background-dark-elevated border border-border-light dark:border-border-dark-subtle rounded-lg p-4">
                        <div className="flex items-center space-x-2 text-sm">
                            <Building className="h-4 w-4 text-primary-500" />
                            <span className="text-text-secondary-light dark:text-text-secondary-dark">
                                Joining organization: <span className="font-medium text-text-primary-light dark:text-text-primary-dark">{organizationName}</span>
                            </span>
                        </div>
                    </div>
                )}

                {/* Password Field */}
                <Input
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Create a strong password"
                    leftIcon={<Lock className="h-4 w-4" />}
                    rightIcon={
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="text-text-muted-light dark:text-text-muted-dark hover:text-primary-500 transition-colors duration-200"
                            disabled={loading}
                        >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                    }
                    required
                    disabled={loading}
                    minLength={8}
                    variant="filled"
                    helperText="Password must be at least 8 characters with uppercase, lowercase, and number"
                />

                {/* Confirm Password Field */}
                <Input
                    label="Confirm Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Confirm your password"
                    leftIcon={<Lock className="h-4 w-4" />}
                    rightIcon={
                        <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="text-text-muted-light dark:text-text-muted-dark hover:text-primary-500 transition-colors duration-200"
                            disabled={loading}
                        >
                            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                    }
                    required
                    disabled={loading}
                    minLength={8}
                    variant="filled"
                />

                {/* Submit Button */}
                <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    loading={loading}
                    disabled={!isLoaded || !signUp}
                >
                    Complete Setup
                </Button>
            </form>

            {/* Back to Login Link */}
            <div className="text-center pt-6 border-t border-border-light dark:border-border-dark-subtle">
                <p className="text-text-secondary-light dark:text-text-secondary-dark">
                    Having trouble?{' '}
                    <Button
                        variant="link"
                        onClick={() => navigate('/auth/login')}
                        className="p-0 h-auto font-medium"
                    >
                        Back to Login
                    </Button>
                </p>
            </div>
        </div>
    );
};

export default AcceptInvitation;